import T from 'prop-types'
import Row from './Row'
import Seat from './Seat'
import { groupRowsByZones } from './zoneUtils'

const isSeatAvailable = seat => (seat?.isEnabled ?? true) && !seat?.isReserved && !seat?.isSelected

function buildSeatList(seats, onAddSeat, onRemoveSeat, isOps) {
  let totalAvailableSeats = 0
  const seatList = seats.map(seat => {
    isSeatAvailable(seat) && ++totalAvailableSeats
    return seat?.label
      ? <Seat
        key={seat.id}
        {...seat}
        isOps={isOps}
        onAddSeat={() => onAddSeat(seat.row, seat.id)}
        onRemoveSeat={() => onRemoveSeat(seat.row, seat.id)}
        monitors={seat.monitors}
      />
      : <div key={seat.label} className="w-[52px] flex-none" />
  })
  return { seatList, hasSeatAvailable: !!totalAvailableSeats }
}

function buildRow(row, seats, onAddSeat, onRemoveSeat, isOps) {
  const { seatList, hasSeatAvailable } = buildSeatList(seats, onAddSeat, onRemoveSeat, isOps)
  row.isSelected = !hasSeatAvailable
  return (
    <Row key={row.id} {...row}>
      {seatList}
    </Row>
  )
}

const ZONE_NAMES = {
  1: 'Dublin', 2: 'Cork', 3: 'Galway', 4: 'Kildare',
  5: 'Meath', 6: 'Limerick', 7: 'Tipperary'
}

const ZoneSection = ({ children }) => (
  <div className="mb-5 border-2 rounded-lg p-[10px]">
    {children}
  </div>
)

ZoneSection.propTypes = { children: T.node.isRequired }

const ZoneLabel = ({ position, children }) => (
  <div
    className="font-bold text-xs text-[#666] mb-[5px]"
    style={{ textAlign: position || 'center' }}
  >
    {children}
  </div>
)

ZoneLabel.propTypes = { position: T.string, children: T.node.isRequired }

const MainUIZoneSection = ({ children }) => (
  <div className="mb-[5px] border-2 rounded-lg p-[8px_9px] relative">
    {children}
  </div>
)

MainUIZoneSection.propTypes = { children: T.node.isRequired }

const SpecialAreaHeader = ({ children }) => (
  <div className="bg-[#e9ecef] text-[#6c757d] w-fit py-2 px-4 mb-[10px] mt-[10px] rounded-lg text-center text-sm border border-[#dee2e6] max-sm:py-[10px] max-sm:px-4 max-sm:text-xs max-sm:mb-[10px]">
    {children}
  </div>
)

SpecialAreaHeader.propTypes = { children: T.node.isRequired }

const SeatMap = ({ data, onAddSeat, onRemoveSeat, isOps, officeName, isGroupBooking = false }) => {
  const showZones = officeName === 'Athlone'

  if (showZones) {
    const zones = groupRowsByZones(data)

    if (isGroupBooking) {
      const leftZones = Object.values(zones).filter(zone => zone.zoneNumber <= 4)
      const rightZones = Object.values(zones).filter(zone => zone.zoneNumber > 4)

      return (
        <div className="flex gap-[40px] justify-between">
          <div className="flex-1">
            {leftZones.toSorted((a, b) => a.zoneNumber - b.zoneNumber).map(zone => (
              <ZoneSection key={zone.zoneNumber}>
                <ZoneLabel>{ZONE_NAMES[zone.zoneNumber]}</ZoneLabel>
                {zone.rows.map(row => {
                  const { seats, ...rowData } = row
                  return buildRow(rowData, seats, onAddSeat, onRemoveSeat, isOps)
                })}
              </ZoneSection>
            ))}
          </div>
          <div className="flex-1">
            {rightZones.toSorted((a, b) => a.zoneNumber - b.zoneNumber).map(zone => (
              <ZoneSection key={zone.zoneNumber}>
                <ZoneLabel>{ZONE_NAMES[zone.zoneNumber]}</ZoneLabel>
                {zone.rows.map(row => {
                  const { seats, ...rowData } = row
                  return buildRow(rowData, seats, onAddSeat, onRemoveSeat, isOps)
                })}
              </ZoneSection>
            ))}
          </div>
        </div>
      )
    }

    const leftZones = Object.values(zones).filter(zone => zone.zoneNumber <= 4)
    const rightZones = Object.values(zones).filter(zone => zone.zoneNumber > 4)

    return (
      <div className="flex gap-10 items-start">
        <div className="flex-none">
          {leftZones.toSorted((a, b) => a.zoneNumber - b.zoneNumber).map(zone => (
            <div key={zone.zoneNumber}>
              {zone.zoneNumber === 1 && (
                <SpecialAreaHeader>Westmeath Board Room</SpecialAreaHeader>
              )}
              <MainUIZoneSection>
                <div className="font-bold text-xs text-[#666] text-center">
                  {ZONE_NAMES[zone.zoneNumber]}
                </div>
                {zone.rows.map(row => {
                  const { seats, ...rowData } = row
                  return buildRow(rowData, seats, onAddSeat, onRemoveSeat, isOps)
                })}
              </MainUIZoneSection>
              {zone.zoneNumber === 4 && (
                <SpecialAreaHeader>Canteen</SpecialAreaHeader>
              )}
            </div>
          ))}
        </div>
        <div className="flex-none">
          <div className='flex justify-end'>
            <SpecialAreaHeader>Huddle Hub</SpecialAreaHeader>
          </div>
          {rightZones.toSorted((a, b) => a.zoneNumber - b.zoneNumber).map(zone => (
            <MainUIZoneSection key={zone.zoneNumber}>
              <div className="font-bold text-xs text-[#666] text-center">
                {ZONE_NAMES[zone.zoneNumber]}
              </div>
              {zone.rows.map(row => {
                const { seats, ...rowData } = row
                return buildRow(rowData, seats, onAddSeat, onRemoveSeat, isOps)
              })}
            </MainUIZoneSection>
          ))}
        </div>
      </div>
    )
  }

  const pairs = []
  for (let i = 0; i < data.length; i += 2) pairs.push(data.slice(i, i + 2))

  return (
    <>
      {pairs.map((pair) => (
        <div key={pair[0].id} className="border rounded-lg p-2 mb-2">
          {pair.map(({ seats, ...row }) => buildRow(row, seats, onAddSeat, onRemoveSeat, isOps))}
        </div>
      ))}
    </>
  )
}

SeatMap.propTypes = {
  data: T.arrayOf(T.object).isRequired,
  onAddSeat: T.func.isRequired,
  onRemoveSeat: T.func.isRequired,
  isOps: T.bool,
  officeName: T.string,
  isGroupBooking: T.bool,
}

export default SeatMap
